import { ReactNode, useCallback, useMemo, useState } from 'react';

import { loaders } from 'entities';

import { useAuth } from '../Auth';

import { useProjectRealTimeSync } from './realTimeSync';
import { ProjectContextData } from './project.context';
import { projectStorage } from './storage';
import { useProjects, projectActions } from './projects.data';

import { toUniqueList } from 'utils/array';
import { recentActionContext, recentContext } from './recent.context';

const RECENT_PROJECT_LIST_SIZE = 10;

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { loginAction } = useAuth();

  const projects = useProjects();
  useProjectRealTimeSync();

  const { error, loading } = loaders.projects.allActiveProjectsV2.use({
    blockLoad: !!loginAction,

    onDataChange: (projects) =>
      // this prevents our richer load from /projects/:id to be overwritten
      // REVISE
      projectActions.setProjects((old) => ({
        ...old,
        ...Object.fromEntries(
          projects.map((project) => [
            project.id,
            {
              ...old[project.id],
              ...project,
              tasks: project.tasks.map((task) => ({
                ...old[project.id]?.tasks.find((oldTask) => oldTask.id === task.id),
                ...task,
              })),
            },
          ]),
        ),
      })),
  });

  const projectList = useMemo(() => Object.values(projects), [projects]);

  const [recentProjects, setRecentProjects] = useState(
    () => projectStorage.recentProjects.get() || [],
  );

  const saveRecentProject = useCallback((id: string) => {
    setRecentProjects((ids) => {
      const updated = toUniqueList([id, ...ids]).slice(0, RECENT_PROJECT_LIST_SIZE);

      projectStorage.recentProjects.set(updated);

      return updated;
    });
  }, []);

  const recentProjectsCtx = useMemo(
    () => ({ recentProjects, saveRecentProject }),
    [recentProjects, saveRecentProject],
  );

  const recentProjectActions = useMemo(() => ({ saveRecentProject }), [saveRecentProject]);

  const context = useMemo(
    () => ({
      projects,
      projectList,
      loadingProjects: loading,
      projectLoadError: error,
      recentProjects,
      saveRecentProject,
      updateProject: projectActions.updateProject,
    }),
    [projects, error, loading, projectList, saveRecentProject, recentProjects],
  );
  recentActionContext;

  return (
    <ProjectContextData.Provider value={context}>
      <recentContext.Provider value={recentProjectsCtx}>
        <recentActionContext.Provider value={recentProjectActions}>
          {children}
        </recentActionContext.Provider>
      </recentContext.Provider>
    </ProjectContextData.Provider>
  );
}
